import * as Immutable from "immutable";
import { noteGroupingSize } from "./grooveUtils";
import { isTripletDivision } from "./utils";

export const EmptyNote = null;
export type EmptyNote = typeof EmptyNote;
export type Note = string | EmptyNote;

interface InstrumentMetadata {
  readonly name: string;
  readonly abc: string;
  readonly note?: string;
}

export interface Instrument {
  readonly name: string;
  readonly metadata: Array<InstrumentMetadata>;
}

const DEFAULT_TEMPO = 50;
const DEFAULT_INSTRUMENTS: Immutable.OrderedSet<
  Instrument
> = Immutable.OrderedSet([
  {
    name: "sticking",
    metadata: [
      { name: "Right", abc: '"R"x' },
      { name: "Left", abc: '"L"x' },
      { name: "Both", abc: '"R/L"x' },
      { name: "Count", abc: '"count"x' },
      { name: "Off", abc: '""x' },
    ],
  },
  {
    name: "hh",
    metadata: [
      { name: "Ride", abc: "^A'", note: "Eb3" },
      { name: "Ride Bell", abc: "^B'", note: "F3" },
      { name: "Cow Bell", abc: "^D'", note: "A7" },
      { name: "Crash", abc: "^c'", note: "Dd3" },
      { name: "Stacker", abc: "^d'", note: "A1" },
      { name: "Open", abc: "!open!^g", note: "Bb2" },
      { name: "Close", abc: "!plus!^g" },
      { name: "Accent", abc: "!accent!^g", note: "C8" },
      { name: "Normal", abc: "^g", note: "Gb2" },
    ],
  },
  {
    name: "snare",
    metadata: [
      { name: "Ghost", abc: "!(.!!).!c", note: "A0" },
      { name: "Accent", abc: "!accent!c", note: "Bb0" },
      { name: "Normal", abc: "c", note: "D2" },
      { name: "Cross Stick", abc: "^c", note: "Db2" },
      { name: "Buzz", abc: "!///!c", note: "Ab7" },
      { name: "Flam", abc: "{/c}c", note: "B7" },
      { name: "Drag", abc: "{/cc}c", note: "B7" },
    ],
  },
  {
    name: "kick",
    metadata: [
      { name: "Kick and Splash", abc: "[F^d,]" },
      { name: "Splash", abc: "^d," },
      { name: "Normal", abc: "F", note: "B1" },
    ],
  },
  {
    name: "Tom 1",
    metadata: [{ name: "T1", abc: "e", note: "F2" }],
  },
  {
    name: "Tom 2",
    metadata: [{ name: "T2", abc: "d", note: "G2" }],
  },
  {
    name: "Tom 3",
    metadata: [{ name: "T3", abc: "B", note: "A2" }],
  },
  {
    name: "Tom 4",
    metadata: [{ name: "T4", abc: "A", note: "B2" }],
  },
]);

function* iterEmptyNotes(count: number) {
  let i = 0;
  while (i++ < count) {
    yield EmptyNote;
  }
}

function createEmptyNoteList(
  notesPerMeasure: number,
  numMeasures: number,
): Immutable.List<EmptyNote> {
  return Immutable.List(iterEmptyNotes(notesPerMeasure * numMeasures));
}

export class DrumData {
  readonly author: string;
  readonly comments: string; // markdown?
  readonly metronomeFrequency: 0 | 4 | 8 | 16;
  readonly noteValue: number; // TimeSigBottom: Bottom part of time signature: 4 = quarter notes, 8 = 8th notes, 16ths, etc..
  readonly numBeats: number; // TimeSigTop: Top part of time signature: 3/4, 4/4, 5/4, 6/8, etc...
  readonly swing: number;
  readonly tempo: number;
  readonly timeDivision: number; // The type of note shown: eighth, eighth triplet, sixteenth, etc...
  readonly title: string;
  readonly instruments: Immutable.OrderedMap<Instrument, Immutable.List<Note>>;

  constructor({
    author = "",
    comments = "",
    instruments = DEFAULT_INSTRUMENTS,
    metronomeFrequency = 0,
    noteValue = 4,
    numBeats = 4,
    swing = 0,
    tempo = DEFAULT_TEMPO,
    timeDivision = 16,
    title = "",
  }: Partial<{
    author: string;
    comments: string;
    instruments: Immutable.OrderedSet<Instrument>;
    metronomeFrequency: 0 | 4 | 8 | 16;
    noteValue: number;
    numBeats: number;
    swing: number;
    tempo: number;
    timeDivision: number;
    title: string;
  }>) {
    this.author = author;
    this.comments = comments;
    this.metronomeFrequency = metronomeFrequency;
    this.noteValue = noteValue;
    this.numBeats = numBeats;
    this.swing = swing;
    this.tempo = tempo;
    this.timeDivision = timeDivision;
    this.title = title;
    this.instruments = Immutable.OrderedMap(
      instruments.map(inst => [
        inst!,
        createEmptyNoteList(this.notesPerMeasure, 2),
      ]),
    );
  }

  get size() {
    return this.instruments.first().size;
  }

  get notesPerMeasure() {
    return this.timeDivision / this.noteValue * this.numBeats;
  }

  get numberOfMeasures() {
    return this.size / this.notesPerMeasure;
  }

  with(other: Partial<DrumData>): DrumData {
    return Object.assign(Object.create((this as any).__proto__), this, other);
  }
}

// split everything up into an array with the indexes of each note
// split by measure, then by note group
export function structureData(
  data: DrumData,
  numberOfMeasures: number = data.numberOfMeasures,
) {
  const notesPerMeasure = data.notesPerMeasure;
  const noteGroupSize = noteGroupingSize(
    notesPerMeasure,
    data.numBeats,
    data.noteValue,
  );
  const fullSize = notesPerMeasure * numberOfMeasures;
  const dataRange = Immutable.Range(0, fullSize, notesPerMeasure);
  const measureRange = Immutable.Range(0, notesPerMeasure, noteGroupSize);
  const structuredNotes = Immutable.Range(0, fullSize).map(i => ({
    i: i!,
    notes: data.instruments.map(notes => notes!.get(i!)),
  }));
  return dataRange.map(measureStart =>
    measureRange.map(noteGroupStart =>
      structuredNotes.slice(
        measureStart! + noteGroupStart!,
        measureStart! + noteGroupStart! + noteGroupSize!,
      ),
    ),
  );
}

export function changeTimeSignature(
  data: DrumData,
  timeSignature: [number, number],
) {
  const [numBeats, noteValue] = timeSignature;

  if (isTripletDivision(data.timeDivision) && noteValue !== 4) {
    alert("not possible");
    // switch to a non triplet division since they are not supported in this time signature
    data = changeDivision(data, 16);
  }

  const newData = data.with({ numBeats, noteValue });
  const oldStructure = structureData(data);
  const newStructure = structureData(newData, data.numberOfMeasures);

  function refillNotes(oldNotes: Immutable.List<Note>, instrument: Instrument) {
    let newNotes: Immutable.List<Note> = Immutable.List();
    newStructure.forEach((measure, measureI) => {
      measure!.forEach((noteGroup, noteGroupI) => {
        noteGroup!.forEach((_, noteI) => {
          let note: Note = EmptyNote;
          const oldMeasure = oldStructure.get(measureI!);
          if (oldMeasure) {
            const oldNoteGroup = oldMeasure.get(noteGroupI!);
            if (oldNoteGroup) {
              const oldNote = oldNoteGroup.get(noteI!);
              if (oldNote) {
                note = oldNote.notes.get(instrument) || EmptyNote;
              }
            }
          }
          newNotes = newNotes.push(note);
        });
      });
    });
    return newNotes;
  }

  return newData.with({
    instruments: data.instruments.map((notes, instrument) =>
      refillNotes(notes!, instrument!),
    ) as Immutable.OrderedMap<Instrument, Immutable.List<Note>>,
  });
}

export function changeDivision(data: DrumData, newDivision: number) {
  if (newDivision === 48) {
    alert(
      "The MIXED subdivision allows you to create a combination of triplets and non-triplet notes in one measure.  Set every 3rd note for 16ths and every 6th note for 8th notes",
    );
  }

  const isNewDivisionTriplets = isTripletDivision(newDivision);
  // check for incompatible odd time signature division, 9/8 and 1/4notes for instance or 9/16 and 1/8notes
  if ((newDivision * data.numBeats / data.noteValue) % 1 !== 0) {
    alert(
      `1/${newDivision} notes are disabled in ${data.numBeats}/${
        data.noteValue
      } time. This combination would result in a half note.`,
    );
    return data;
  }

  if (isNewDivisionTriplets && data.noteValue !== 4) {
    alert(
      `Triplets are disabled in ${data.numBeats}/${
        data.noteValue
      } time. Use x/4 time for triplets.`,
    );
    return data;
  }

  const newData = data.with({ timeDivision: newDivision });
  const oldStructure = structureData(data);
  const newStructure = structureData(newData, data.numberOfMeasures);

  function refillNotes(oldNotes: Immutable.List<Note>, instrument: Instrument) {
    let newNotes: Immutable.List<Note> = Immutable.List();
    newStructure.forEach((measure, measureI) => {
      measure!.forEach((noteGroup, noteGroupI) => {
        noteGroup!.forEach((note, noteI) => {
          const oldNote = oldStructure
            .get(measureI!)
            .get(noteGroupI!)
            .get(noteI!);
          newNotes = newNotes.push(
            (oldNote ? oldNote.notes.get(instrument) : EmptyNote) || EmptyNote,
          );
        });
      });
    });
    return newNotes;
  }

  if (isTripletDivision(data.timeDivision) === isNewDivisionTriplets) {
    return newData.with({
      instruments: data.instruments.map((notes, instrument) =>
        refillNotes(notes!, instrument!),
      ) as Immutable.OrderedMap<Instrument, Immutable.List<Note>>,
    });

    // TODO think about this old comment
    // override the hi-hat if we are going to a higher division.
    // otherwise the notes get lost in translation (not enough)
    // if (newDivision > class_notes_per_measure)
    // 	uiHH = root.myGrooveUtils.GetDefaultHHGroove(new_notes_per_measure, grooveData.numBeats, grooveData.noteValue, class_number_of_measures);
  } else {
    // TODO think about using default because of the following old comment
    // changing from or changing to a triplet division
    // triplets don't scale well, so use defaults when we change

    return newData.with({
      instruments: data.instruments.map((notes, instrument) =>
        refillNotes(notes!, instrument!),
      ) as Immutable.OrderedMap<Instrument, Immutable.List<Note>>,
    });
  }
}



// WEBPACK FOOTER //
// ./src/data.ts